import React, { useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Cache a nivel de módulo: una sola consulta de pasarelas por sesión de
// navegación, compartida por todas las instancias de PaymentLogosBar.
let _gatewaysPromise = null;
export function fetchGateways() {
  if (!_gatewaysPromise) {
    _gatewaysPromise = fetch(`${BACKEND_URL}/api/payments/gateways`)
      .then(res => (res.ok ? res.json() : { gateways: [{ id: 'azul' }] }))
      .catch(() => ({ gateways: [{ id: 'azul' }] }));
  }
  return _gatewaysPromise;
}

/* ── Logos de tarjetas y sellos 3-D Secure ─────────────────────────
   Estilo unificado y acorde a la línea de diseño del sitio:
   · Fichas blancas redondeadas con borde sutil (como las tarjetas del sitio).
   · Tipografía 'DM Sans' (la del sitio) — sin Arial.
   · SVG con viewBox, por lo que escalan perfecto con la prop className (h-6, h-8…).
   Todos aceptan className para el tamaño externo.
──────────────────────────────────────────────────────────────────── */

const FONT = "'Satoshi', system-ui, -apple-system, sans-serif";

export function VisaLogo({ className = 'h-7' }) {
  return (
    <svg className={className} viewBox="0 0 80 48" xmlns="http://www.w3.org/2000/svg" aria-label="Visa" role="img">
      <rect x="0.5" y="0.5" width="79" height="47" rx="8" fill="#FFFFFF" stroke="#E5E7EB" />
      <text x="40" y="32" textAnchor="middle" fontFamily={FONT} fontWeight="800" fontStyle="italic" fontSize="22" letterSpacing="0.5" fill="#1A1F71">VISA</text>
    </svg>
  );
}

export function MastercardLogo({ className = 'h-7' }) {
  return (
    <svg className={className} viewBox="0 0 80 48" xmlns="http://www.w3.org/2000/svg" aria-label="Mastercard" role="img">
      <rect x="0.5" y="0.5" width="79" height="47" rx="8" fill="#FFFFFF" stroke="#E5E7EB" />
      <circle cx="33" cy="24" r="13" fill="#EB001B" />
      <circle cx="47" cy="24" r="13" fill="#F79E1B" />
      <path d="M40 14a13 13 0 0 1 0 20 13 13 0 0 1 0-20z" fill="#FF5F00" />
    </svg>
  );
}

export function AzulBadge({ className = 'h-7' }) {
  return (
    <svg className={className} viewBox="0 0 80 48" xmlns="http://www.w3.org/2000/svg" aria-label="Azul Procesadora" role="img">
      <rect width="80" height="48" rx="8" fill="#0B57A4" />
      <text x="40" y="31" textAnchor="middle" fontFamily={FONT} fontWeight="800" fontSize="18" letterSpacing="2" fill="#FFFFFF">AZUL</text>
    </svg>
  );
}

export function VerifiedByVisa({ className = 'h-8' }) {
  return (
    <svg className={className} viewBox="0 0 132 48" xmlns="http://www.w3.org/2000/svg" aria-label="Verified by Visa" role="img">
      <rect x="0.5" y="0.5" width="131" height="47" rx="8" fill="#FFFFFF" stroke="#E5E7EB" />
      <text x="66" y="19" textAnchor="middle" fontFamily={FONT} fontSize="9" fontWeight="600" fill="#6B7280">Verified by</text>
      <text x="66" y="39" textAnchor="middle" fontFamily={FONT} fontSize="19" fontWeight="800" fontStyle="italic" letterSpacing="0.5" fill="#1A1F71">VISA</text>
    </svg>
  );
}

export function MastercardIDCheck({ className = 'h-8' }) {
  return (
    <svg className={className} viewBox="0 0 150 48" xmlns="http://www.w3.org/2000/svg" aria-label="Mastercard Identity Check" role="img">
      <rect x="0.5" y="0.5" width="149" height="47" rx="8" fill="#FFFFFF" stroke="#E5E7EB" />
      <circle cx="22" cy="24" r="11" fill="#EB001B" />
      <circle cx="34" cy="24" r="11" fill="#F79E1B" />
      <path d="M28 15a11 11 0 0 1 0 18 11 11 0 0 1 0-18z" fill="#FF5F00" />
      <text x="52" y="20" fontFamily={FONT} fontSize="9" fontWeight="600" fill="#6B7280">Mastercard</text>
      <text x="52" y="37" fontFamily={FONT} fontSize="14" fontWeight="800" fill="#134168">ID Check</text>
    </svg>
  );
}

export function KlarnaBadge({ className = 'h-7' }) {
  return (
    <svg className={className} viewBox="0 0 80 48" xmlns="http://www.w3.org/2000/svg" aria-label="Klarna" role="img">
      <rect width="80" height="48" rx="8" fill="#FFB3C7" />
      <text x="40" y="31" textAnchor="middle" fontFamily={FONT} fontWeight="800" fontSize="16" letterSpacing="0.3" fill="#17120F">Klarna</text>
    </svg>
  );
}

export function StripeBadge({ className = 'h-7' }) {
  return (
    <svg className={className} viewBox="0 0 80 48" xmlns="http://www.w3.org/2000/svg" aria-label="Stripe" role="img">
      <rect width="80" height="48" rx="8" fill="#635BFF" />
      <text x="40" y="31" textAnchor="middle" fontFamily={FONT} fontWeight="800" fontSize="16" letterSpacing="0.3" fill="#FFFFFF">stripe</text>
    </svg>
  );
}

/* Barra compacta con todos los logos — úsala en el checkout y homepage.
   Klarna/Stripe aparecen automáticamente cuando la pasarela está activa
   en el backend (no hay que tocar el frontend al encenderla). */
export function PaymentLogosBar({ show3DS = false, className = '' }) {
  const [stripeOn, setStripeOn] = useState(false);
  useEffect(() => {
    let mounted = true;
    fetchGateways().then(data => {
      if (mounted && (data.gateways || []).some(g => g.id === 'stripe')) setStripeOn(true);
    });
    return () => { mounted = false; };
  }, []);

  return (
    <div className={`flex flex-wrap items-center justify-center gap-3 ${className}`}>
      <VisaLogo className="h-7" />
      <MastercardLogo className="h-7" />
      <AzulBadge className="h-7" />
      {stripeOn && (
        <>
          <StripeBadge className="h-7" />
          <KlarnaBadge className="h-7" />
        </>
      )}
      {show3DS && (
        <>
          <VerifiedByVisa className="h-8" />
          <MastercardIDCheck className="h-8" />
        </>
      )}
    </div>
  );
}
